import {
  Box, HStack, VStack, IconButton, Menu, MenuButton, MenuItem, MenuList, Portal,
} from "@chakra-ui/react";
import CDS from "design-system";
import { FiMoreVertical } from "react-icons/fi";
import { MdDragIndicator } from "react-icons/md";
import { Link as RouterLink, generatePath } from "react-router";
import { PEOPLE_TEAM_ROUTES } from "definitions/constants/routeConstants";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import RoleLabelTag from "components/Tag/UserTag/RoleLabelTag";
import type { DensityMode, GridJourney, GridTask, GridTaskTemplate, GridUser } from "../types";
import TaskCell from "./TaskCell";
import TaskTemplateHoverCard from "./TaskTemplateHoverCard";

const ROW_PREFIX = "row:";

interface RowGroupProps {
  template: GridTaskTemplate;
  journeys: GridJourney[];
  cellIndex: Map<string, GridTask>;
  assigneeIndex: Map<string, GridUser>;
  role: string | null;
  assigneeUser: GridUser | null;
  descriptors: { start: string | null; due: string | null };
  showFullRoleTag: boolean;
  density: DensityMode;
  onHideTemplate: (id: number) => void;
  today: Date;
}

export default function RowGroup({
  template, journeys, cellIndex, assigneeIndex, role, assigneeUser, descriptors, showFullRoleTag, density, onHideTemplate, today,
}: RowGroupProps) {
  const sortableId = `${ROW_PREFIX}${template.id}`;
  const {
    setNodeRef, setActivatorNodeRef, attributes, listeners, transition, transform, isDragging,
  } = useSortable({ id: sortableId });

  const rowOpacity = isDragging ? 0.3 : 1;
  // Apply the transform to every cell so the whole row shifts in unison as
  // siblings are rearranged around the dragged item. Skip the transform on the
  // dragged row itself — DragOverlay is rendering the floating preview, so the
  // original row should stay put (just dimmed).
  const cellTransition: React.CSSProperties = isDragging
    ? { transition, opacity: rowOpacity }
    : { transition, transform: CSS.Transform.toString(transform), opacity: rowOpacity };

  return (
    <>
      <Box
        ref={setNodeRef}
        position="sticky"
        left={0}
        zIndex={1}
        bg="bg.primary"
        borderBottom="1px solid"
        borderColor="gray.100"
        pl={2}
        pr={1}
        py={density === "compact" ? 0.5 : 1.5}
        style={cellTransition}
      >
        <HStack spacing={2} align="center">
          <Box
            ref={setActivatorNodeRef}
            {...attributes}
            {...listeners}
            color="gray.300"
            cursor="grab"
            _active={{ cursor: "grabbing" }}
            display="flex"
            alignItems="center"
            justifyContent="center"
            _hover={{ color: "gray.500" }}
            p={1}
            minW="24px"
            minH="24px"
            style={{ touchAction: "none" }}
          >
            <Box as={MdDragIndicator} boxSize={4} aria-label="Drag to reorder task" />
          </Box>
          <VStack align="start" spacing={density === "compact" ? 0 : 0.5} flex={1} minW={0}>
            <HStack spacing={2} align="center" w="100%" minW={0}>
              {density === "compact" && (
                <RoleLabelTag role={role} user={assigneeUser} size="xs" iconOnly />
              )}
              <TaskTemplateHoverCard template={template} role={role} assigneeUser={assigneeUser} descriptors={descriptors}>
                <Box as="span" display="block" minW={0} w="100%">
                  <CDS.Text12
                    as={RouterLink}
                    to={generatePath(PEOPLE_TEAM_ROUTES.taskTemplates.show, { id: String(template.id) })}
                    fontWeight="medium"
                    noOfLines={1}
                    color="blue.600"
                    w="100%"
                    _hover={{ textDecoration: "underline" }}
                  >
                    {template.name}
                  </CDS.Text12>
                </Box>
              </TaskTemplateHoverCard>
            </HStack>
            {density === "comfortable" && (
              <HStack spacing={2} align="center" minW={0}>
                <RoleLabelTag role={role} user={assigneeUser} size="xs" iconOnly={!showFullRoleTag} />
                {(descriptors.start || descriptors.due) && (
                  <CDS.Text10 color="gray.500" noOfLines={1}>
                    {descriptors.start || descriptors.due}
                  </CDS.Text10>
                )}
              </HStack>
            )}
          </VStack>
          <Menu placement="bottom-end">
            <MenuButton
              as={IconButton}
              aria-label="Task actions"
              icon={<FiMoreVertical size={14} />}
              size="xs"
              variant="ghost"
              minW="20px"
              h="20px"
              px={0}
            />
            <Portal>
              <MenuList>
                <MenuItem onClick={() => onHideTemplate(template.id)}>Remove from view</MenuItem>
              </MenuList>
            </Portal>
          </Menu>
        </HStack>
      </Box>
      {journeys.map((journey) => {
        const task = cellIndex.get(`${journey.id}:${template.id}`);
        const assignee = task?.assigneeId ? assigneeIndex.get(String(task.assigneeId)) : undefined;
        return (
          <Box key={`${journey.id}:${template.id}`} style={cellTransition}>
            <TaskCell
              task={task}
              assignee={assignee}
              today={today}
              fallbackRole={role}
              density={density}
            />
          </Box>
        );
      })}
      <Box borderBottom="1px solid" borderColor="gray.100" style={cellTransition} />
    </>
  );
}
